/**
 * Tabela de saldo de caixa projetado nos proximos 6 meses.
 *
 * Pos-#73: ganhou coluna POSICAO ABERTA (MtM volume × curva forward).
 * Pos-#99: alinhada inteira no eixo de CAIXA (a tela se chama Fluxo de
 * Caixa). Coluna nova MES FORN. mostra qual mes de fornecimento esta
 * sendo liquidado em cada mes de caixa (caixa - 1 — regra padrao CCEE).
 * Tabela passou a ter 7 colunas; alinhamento via `table-layout: fixed`
 * + width % por coluna pra manter cells consistentes mesmo com valores
 * de larguras diferentes.
 *
 * Aggregation de entradas/saidas continua client-side (mock saldoInicialCaixa
 * via projecaoSaldo6Meses); MtM vem do backend (/api/fluxo/posicao-aberta-mtm-6m).
 * Decisao Veiga 30/04: enquanto saldoInicialCaixa for mock, full backend
 * (opcao 1) seria refator prematuro. Quando Pluggy entrar, abrimos task
 * pra mover toda a logica pro server.
 *
 * Freshness guard (Veiga): MtM depende do cron zeus_volume_total (#96). Se
 * stale (>=26h) ou error (>=72h ou null), badge ao lado do header da
 * coluna + valor com opacidade reduzida — usuario precisa ver que esta
 * ruim, nao que sumiu.
 *
 * Cor SALDO FINAL (#99): positivo turquesa, negativo coral, zero cinza
 * — antes era sempre branco, dificultava ler tendencia visual.
 *
 * Pos-#100: cell POSICAO ABERTA virou layout vertical igual o card
 * "Proximo 6du" (#80b) — valor R$ no topo + segunda linha com badge
 * SHORT/LONG/FLAT + MWm. Reusa <PosicaoBadge>. Decisao Veiga: mostrar
 * so MWm (nao MWh) na tabela pra nao poluir; card mantem MWh + MWm
 * porque tem mais espaco vertical.
 */
import { Info } from "lucide-react";
import type { AsOfMeta } from "../../api/types";
import { AsOfBadge } from "../ui/AsOfBadge";
import type { ProjecaoMes } from "../../utils/mockData";
import { fmtBRL, fmtMWm } from "../../utils/formatters";
import {
  classifyFreshness,
  formatHoursAgo,
  hoursSince,
  type FreshnessState,
} from "../../utils/freshness";
import { PosicaoBadge } from "./PosicaoBadge";

/** MtM por mes (lookup via chave de mes_caixa, ex: "Jul/26") + label de
 * mes de fornecimento correspondente pra exibir na coluna MES FORN. */
export interface MtmPorMes {
  mtm: number;
  /** Label curta tipo "Jun/26" — backend devolve mes_caixa + mes_fornecimento;
   * page formata. null/undefined renderiza como "—". */
  mesFornecimentoLabel?: string;
  /** Volume liquido em MWh (sinal natural) — alimenta o badge SHORT/LONG.
   * 0 ou ausente → badge nao renderiza (FLAT silencioso). */
  volumeMwh?: number;
  /** Volume medio em MWm — exibido em texto pequeno ao lado do badge.
   * Ausente → linha secundaria nao renderiza. */
  volumeMwm?: number;
}

interface Props {
  rows: ProjecaoMes[];
  /** Lookup MtM por chave de mes_caixa (formato igual ao `rows[i].mes`,
   * ex: "Mai/26"). Mes ausente do lookup → mtm=0, label vazio. */
  mtmByMonth?: Record<string, MtmPorMes>;
  /** ISO 8601 do MAX(finished_at) do cron zeus_volume_total. null quando
   * cron nunca rodou — UI renderiza badge VERMELHA. */
  lastSync?: string | null;
  /** #60: as-of do payload do fluxo (a série que alimenta esta tabela).
   * undefined = ainda carregando (sem badge); null = resposta sem meta →
   * o badge DIZ "as-of desconhecido". */
  asOf?: AsOfMeta | null;
}

const TOOLTIP_HEADER =
  "MtM da posicao aberta cuja liquidacao acontece neste mes de caixa " +
  "(= fornecimento do mes anterior). SHORT (negativo) = a comprar. " +
  "LONG (positivo) = a vender.";

const TOOLTIP_MES_FORN =
  "Mes de fornecimento da posicao aberta cuja liquidacao cai neste mes " +
  "de caixa (regra padrao CCEE: fornecimento mes N → 6du do mes N+1).";

// Task #161: a Tabela agrega o MÊS inteiro; o Card "Próximo 6DU" mostra só
// o DIA da liquidação. Tooltip + subtítulo explicitam o escopo pra não
// parecer divergência com o Card.
//
// Task #194: corrigido o rótulo enganoso de #161. O spike #162 confirmou que
// esta tabela só soma Zeus (trades 6DU) + Posição Aberta (MtM) — NÃO inclui
// vencimentos Omie (CR/CP). O texto anterior dizia "OMIE + ZEUS 6DU", o que
// induzia leitura errada. Cálculo inalterado; só o rótulo passou a refletir
// o que o código de fato agrega.
const ESCOPO_TOOLTIP =
  "Agrega o MÊS inteiro: trades Zeus que liquidam no 6DU do mês + Posição " +
  "Aberta (MtM). Vencimentos Omie (CR/CP do mês) NÃO entram nesta tabela — " +
  "ver o gráfico Fluxo 30d para essa visão. Para o detalhe do dia do 6DU, " +
  "ver o Card 'Próximo 6DU' acima.";

/** Cor por sinal — reusa a paleta ja aplicada em Entradas/Saidas. */
function classBySign(value: number): string {
  if (value < 0) return "text-red-400";
  if (value > 0) return "text-indra-turquesa";
  return "text-slate-500";
}

function badgeText(state: FreshnessState, lastSync: string | null | undefined): string {
  const ago = formatHoursAgo(hoursSince(lastSync ?? null));
  if (state === "stale") return `ESTIMATIVA STALE — ultima atualizacao ${ago}`;
  return `DADO INDISPONIVEL — sync de Posicao Aberta parado ${ago === "—" ? "" : `ha ${ago}`}`.trim();
}

export function SaldoProjetadoTable({ rows, mtmByMonth, lastSync, asOf }: Props) {
  const freshness: FreshnessState = classifyFreshness(lastSync ?? null);
  const showBadge = freshness !== "fresh";
  const mtmOpacity = freshness === "fresh" ? "" : "opacity-60";

  // Recalcula variacao + saldo cumulativo client-side com MtM injetado.
  let saldoAcum: number | null = null;
  const enriched = rows.map((r, i) => {
    const entry = mtmByMonth?.[r.mes];
    const mtm = entry?.mtm ?? 0;
    const variacao = r.entradas - r.saidas + mtm;
    if (i === 0) {
      // Primeiro mes: saldo "antes da variacao" derivado do mock
      // (saldoFinal_orig - variacao_orig) + nova variacao.
      const saldoInicialMes = r.saldoFinal - r.variacao;
      saldoAcum = saldoInicialMes + variacao;
    } else {
      saldoAcum = (saldoAcum ?? 0) + variacao;
    }
    return {
      ...r,
      mtm,
      mesFornecimentoLabel: entry?.mesFornecimentoLabel ?? "",
      volumeMwh: entry?.volumeMwh,
      volumeMwm: entry?.volumeMwm,
      variacao,
      saldoFinal: saldoAcum,
    };
  });

  return (
    <div className="border border-slate-700/40 rounded-lg p-4 bg-indra-azul-surface">
      <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
            Saldo projetado — próximos 6 meses
            <span
              className="cursor-help"
              title={ESCOPO_TOOLTIP}
              data-testid="saldo6m-escopo-tooltip"
            >
              <Info size={14} className="text-slate-400" />
            </span>
            {/* #60: as-of do DADO da série do fluxo (undefined = carregando) */}
            {asOf !== undefined && <AsOfBadge asOf={asOf} />}
          </h3>
          <p
            className="text-sm font-medium tracking-wider text-slate-400/80 mt-0.5"
            data-testid="saldo6m-escopo-subtitulo"
          >
            MÊS INTEIRO · ZEUS 6DU + POSIÇÃO ABERTA (MtM)
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            Projeção baseada em fluxo mensal consolidado Atacado + Varejo. Eixo: caixa.
          </p>
        </div>
        {showBadge && (
          <span
            data-testid="mtm-freshness-badge"
            data-freshness={freshness}
            className={
              "text-[11px] uppercase tracking-wide px-2 py-1 rounded-md font-semibold " +
              (freshness === "stale"
                ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                : "bg-red-500/20 text-red-300 border border-red-500/40")
            }
            title={badgeText(freshness, lastSync)}
          >
            {badgeText(freshness, lastSync)}
          </span>
        )}
      </div>

      <div className="overflow-x-auto">
        {/* table-layout: fixed garante que colunas respeitem o `colgroup`
            independente do conteudo das cells — sem isso o browser
            recalculava cada linha e desalinhava colunas longas tipo
            "POSIÇÃO ABERTA (estimativa)". */}
        {/* min-w: abaixo de ~640px o wrapper overflow-x-auto ENGAJA (scroll)
            em vez de esmagar as 7 colunas fixas (gate 768, review 03/07). */}
        <table className="w-full min-w-[640px]" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "10%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "18%" }} />
          </colgroup>
          <thead>
            <tr className="border-b border-slate-700/50">
              <th className="text-left py-2 px-3 text-xs tracking-wide text-slate-400 font-medium whitespace-nowrap">
                Mês
              </th>
              <th
                className="text-left py-2 px-3 text-xs tracking-wide text-slate-500 font-medium whitespace-nowrap"
                title={TOOLTIP_MES_FORN}
              >
                Mês Forn.
              </th>
              <th className="text-right py-2 px-3 text-xs tracking-wide text-slate-400 font-medium whitespace-nowrap">
                Entradas
              </th>
              <th className="text-right py-2 px-3 text-xs tracking-wide text-slate-400 font-medium whitespace-nowrap">
                Saídas
              </th>
              <th
                className="text-right py-2 px-3 text-xs tracking-wide text-slate-400 font-medium whitespace-nowrap"
                title={TOOLTIP_HEADER}
              >
                Posição Aberta
              </th>
              <th className="text-right py-2 px-3 text-xs tracking-wide text-slate-400 font-medium whitespace-nowrap">
                Variação
              </th>
              <th className="text-right py-2 px-3 text-xs tracking-wide text-slate-400 font-medium whitespace-nowrap">
                Saldo Final
              </th>
            </tr>
          </thead>
          <tbody>
            {enriched.map((r, i) => {
              const isLast = i === enriched.length - 1;
              const mtmCls = classBySign(r.mtm);
              const variacaoCls = classBySign(r.variacao);
              const saldoCls = classBySign(r.saldoFinal);
              return (
                <tr
                  key={r.mes}
                  className={
                    "border-b border-slate-700/20 hover:bg-white/5 " +
                    (isLast ? "bg-white/5" : "")
                  }
                >
                  <td className="py-2.5 px-3 text-sm text-slate-300 font-medium whitespace-nowrap">{r.mes}</td>
                  <td
                    data-testid={`mes-forn-cell-${r.mes}`}
                    className="py-2.5 px-3 text-sm text-slate-500 whitespace-nowrap"
                  >
                    {r.mesFornecimentoLabel || "—"}
                  </td>
                  <td className="py-2.5 px-3 text-right text-sm font-mono tabular-nums text-indra-turquesa whitespace-nowrap">
                    {fmtBRL(r.entradas, { abbreviate: true })}
                  </td>
                  <td className="py-2.5 px-3 text-right text-sm font-mono tabular-nums text-red-400 whitespace-nowrap">
                    {fmtBRL(r.saidas, { abbreviate: true })}
                  </td>
                  {/* Task #100: layout vertical — valor R$ no topo,
                      segunda linha [BADGE SHORT/LONG] MWm em texto menor.
                      Espelha o card "Proximo 6du" (SixDuCard #80b). Sem
                      volume, so renderiza o R$ (FLAT silencioso). */}
                  <td
                    data-testid={`mtm-cell-${r.mes}`}
                    className={`py-2.5 px-3 text-right text-sm font-mono tabular-nums whitespace-nowrap ${mtmCls} ${mtmOpacity}`}
                  >
                    <div>{fmtBRL(r.mtm, { abbreviate: true })}</div>
                    {r.volumeMwh !== undefined && r.volumeMwh !== 0 && (
                      <div
                        className="mt-0.5 flex items-center justify-end gap-1.5 text-[11px] opacity-90"
                        data-testid={`mtm-cell-${r.mes}-volume`}
                      >
                        <PosicaoBadge
                          volumeMwh={r.volumeMwh}
                          testIdPrefix={`mtm-cell-${r.mes}-badge`}
                        />
                        {r.volumeMwm !== undefined && (
                          <span className="text-slate-400">{fmtMWm(r.volumeMwm)}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className={`py-2.5 px-3 text-right text-sm font-mono tabular-nums whitespace-nowrap ${variacaoCls}`}>
                    {fmtBRL(r.variacao, { abbreviate: true })}
                  </td>
                  <td
                    data-testid={`saldo-final-cell-${r.mes}`}
                    className={`py-2.5 px-3 text-right text-sm font-mono tabular-nums whitespace-nowrap font-semibold ${saldoCls}`}
                  >
                    {fmtBRL(r.saldoFinal, { abbreviate: true })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
